import { useApi } from './api';
import { URLS } from '../constants/urls';

export function useReportAssetService() {
  const { authenticatedFetch, authenticatedFormFetch } = useApi();

  // ====== CREATE (multipart) ======
  // formData fields esperados:
  // - file (PDF obrigatório no create)
  // - algoritmoId (codificado)
  // - profileId? (string ou vazio p/ comum)
  // - sortOrder?, version?, isActive?, pageRanges? (JSON string ou array)
  const criarAsset = async (formData) => {
    const res = await authenticatedFormFetch(URLS.CREATE_REPORT_ASSET, formData);
    return res.json();
  };

  // ====== UPDATE (multipart, arquivo opcional) ======
  // formData fields:
  // - id (string PK do asset)
  // - algoritmoId (codificado)
  // - file? (PDF novo opcional)
  // - profileId?, sortOrder?, version?, isActive?, pageRanges? (JSON string ou array)
  const atualizarAsset = async (formData) => {
    const res = await authenticatedFormFetch(URLS.UPDATE_REPORT_ASSET, formData);
    return res.json();
  };

  // ====== DELETE (apaga registro + arquivo físico) ======
  const deletarAsset = async (id) => {
    const res = await authenticatedFetch(URLS.DELETE_REPORT_ASSET, {
      method: 'POST',
      body: JSON.stringify({ id }),
    });
    return res.json();
  };

  // ====== READ (GET por ID) ======
  const obterAssetPorId = async (id) => {
    const url = new URL(URLS.READ_REPORT_ASSET);
    url.searchParams.set('id', id);
    const res = await authenticatedFetch(url.toString(), { method: 'GET' });
    return res.json();
  };

  // ====== LISTAGENS ======

  // Comuns por algoritmo (profile_id IS NULL)
  const listarComunsPorAlgoritmo = async (algoritmoId) => {
    const url = new URL(URLS.LIST_COMMONS_BY_ALG);
    url.searchParams.set('algoritmoId', algoritmoId); // codificado
    const res = await authenticatedFetch(url.toString(), { method: 'GET' });
    return res.json();
  };

  // Por perfil específico
  const listarPorPerfil = async (algoritmoId, profileId) => {
    const url = new URL(URLS.LIST_BY_PROFILE);
    url.searchParams.set('algoritmoId', algoritmoId); // codificado
    url.searchParams.set('profileId', profileId);
    const res = await authenticatedFetch(url.toString(), { method: 'GET' });
    return res.json();
  };

  // Todos de um algoritmo (admin)
  const listarTodosDoAlgoritmo = async (algoritmoId, { onlyActive } = {}) => {
    const url = new URL(URLS.LIST_ALL_FOR_ALG);
    url.searchParams.set('algoritmoId', algoritmoId); // codificado
    if (typeof onlyActive !== 'undefined') {
      url.searchParams.set('onlyActive', onlyActive ? '1' : '0');
    }
    const res = await authenticatedFetch(url.toString(), { method: 'GET' });
    return res.json();
  };

  // ====== META/STATUS ======

  // Atualiza apenas storageUri + pageRanges (sem upload)
  const atualizarFileMeta = async ({ id, storageUri, pageRanges }) => {
    const body = { id, storageUri };
    if (typeof pageRanges !== 'undefined') body.pageRanges = pageRanges; // string JSON ou array
    const res = await authenticatedFetch(URLS.UPDATE_FILE_META, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return res.json();
  };

  // Ativa/Desativa (soft)
  const setAtivo = async (id, active) => {
    const res = await authenticatedFetch(URLS.SET_ACTIVE_REPORT_ASSET, {
      method: 'POST',
      body: JSON.stringify({ id, active: !!active }),
    });
    return res.json();
  };

  // Reordenar grupo (comuns OU de um perfil)
  // orderedIds = array de IDs na ordem desejada
  const reordenarGrupo = async ({ algoritmoId, profileId = null, orderedIds }) => {
    const res = await authenticatedFetch(URLS.REORDER_REPORT_ASSET_GROUP, {
      method: 'POST',
      body: JSON.stringify({ algoritmoId, profileId, orderedIds }),
    });
    return res.json();
  };

  // ====== COMPOSIÇÃO ======

  // Retorna a lista final (comuns + blocos por perfil na ordem fornecida)
  const obterFontesParaComposicao = async ({ algoritmoId, profileOrder }) => {
    const res = await authenticatedFetch(URLS.COMPOSE_SOURCES_LIST, {
      method: 'POST',
      body: JSON.stringify({ algoritmoId, profileOrder }),
    });
    return res.json();
  };

  // Busca com filtros (grid/admin)
  // params: { algoritmoId, profileId?, active?, limit?, offset? }
  const buscarAssets = async (params) => {
    const url = new URL(URLS.SEARCH_REPORT_ASSETS);
    if (!params?.algoritmoId) throw new Error('algoritmoId é obrigatório');
    url.searchParams.set('algoritmoId', params.algoritmoId); // codificado
    if (typeof params.profileId !== 'undefined' && params.profileId !== null) {
      url.searchParams.set('profileId', params.profileId);
    }
    if (typeof params.active !== 'undefined' && params.active !== null) {
      url.searchParams.set('active', params.active ? '1' : '0');
    }
    if (typeof params.limit !== 'undefined') {
      url.searchParams.set('limit', String(params.limit));
    }
    if (typeof params.offset !== 'undefined') {
      url.searchParams.set('offset', String(params.offset));
    }
    const res = await authenticatedFetch(url.toString(), { method: 'GET' });
    return res.json();
  };

  return {
    // CRUD com arquivo
    criarAsset,
    atualizarAsset,
    deletarAsset,

    // Reads/Listagens
    obterAssetPorId,
    listarComunsPorAlgoritmo,
    listarPorPerfil,
    listarTodosDoAlgoritmo,

    // Meta/Status
    atualizarFileMeta,
    setAtivo,
    reordenarGrupo,

    // Composição
    obterFontesParaComposicao,
    buscarAssets,
  };
}